import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@jhool-io/fe-components";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useAddRemarkCodes } from "../../hooks/remark-code.mutations";
import { IAddRemarkCode } from "../../types/remark-codes.types";
import useToast from "../../../../hooks/useToast";

interface AddRemarkCodeProps {
    onFormSubmit: () => void;
}

const schema = yup.object().shape({
    code: yup.string().required("Remark code is required"),
    description: yup.string().required("Description is required"),
});

export default function AddRemarkCode({ onFormSubmit }: AddRemarkCodeProps) {
    const addRemarkCode = useAddRemarkCodes();
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const {
        register,
        formState: { errors },
        handleSubmit,
    } = useForm<IAddRemarkCode>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const onSubmit = (data: IAddRemarkCode) => {
        addRemarkCode.mutate(data, {
            onSuccess(res) {
                queryClient.invalidateQueries({ queryKey: ["remark-codes"] });
                toast({
                    mode: "success",
                    message: res.message || "Remark code created successfully",
                });
                onFormSubmit();
            },
            onError(err) {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not add remark code, please try again",
                });
            },
        });
    };

    return (
        <form id="add-remark-code" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <Input
                    label="Remark Code"
                    {...register("code")}
                    hasError={!!errors.code}
                    errorText={errors.code?.message}
                />
            </div>
            <div className="fg">
                <Input
                    label="Description"
                    {...register("description")}
                    hasError={!!errors.description}
                    errorText={errors.description?.message}
                />
            </div>
        </form>
    );
}
